import { Injectable, inject, signal, computed } from '@angular/core';
import { SettingsService } from '../../global/settings';

const STORAGE_KEY = 'abs_impersonation';

@Injectable({ providedIn: 'root' })
export class ImpersonationService {
    private settingsService = inject(SettingsService);

    private originalRoles = signal<string[] | null>(null);
    private impersonatedRoles = signal<string[]>([]);

    isImpersonating = computed(() => this.originalRoles() !== null);
    currentImpersonatedRoles = this.impersonatedRoles.asReadonly();

    constructor() {
        this.restoreFromStorage();
    }

    startImpersonation(roles: string[]) {
        const login = this.settingsService.login();
        if (!login?.user) {
            return;
        }

        if (this.originalRoles() === null) {
            this.originalRoles.set([...(login.user.roles || [])]);
        }

        this.impersonatedRoles.set(roles);
        this.saveToStorage(this.originalRoles(), roles);

        this.settingsService.updateValue('global', 'login', {
            ...login,
            user: { ...login.user, roles: roles }
        });
    }

    updateRoles(roles: string[]) {
        this.startImpersonation(roles);
    }

    stopImpersonation() {
        const login = this.settingsService.login();
        if (!login?.user || this.originalRoles() === null) {
            return;
        }

        this.settingsService.updateValue('global', 'login', {
            ...login,
            user: { ...login.user, roles: this.originalRoles() }
        });

        this.originalRoles.set(null);
        this.impersonatedRoles.set([]);
        localStorage.removeItem(STORAGE_KEY);
    }

    private restoreFromStorage() {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (!stored) return;
            const { originalRoles, impersonatedRoles } = JSON.parse(stored);
            if (originalRoles && impersonatedRoles) {
                this.originalRoles.set(originalRoles);
                this.impersonatedRoles.set(impersonatedRoles);
                // Defer applying roles until login state is available
                setTimeout(() => {
                    const login = this.settingsService.login();
                    if (login?.user) {
                        this.settingsService.updateValue('global', 'login', {
                            ...login,
                            user: { ...login.user, roles: impersonatedRoles }
                        });
                    }
                });
            }
        } catch {
            localStorage.removeItem(STORAGE_KEY);
        }
    }

    private saveToStorage(originalRoles: string[], impersonatedRoles: string[]) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ originalRoles, impersonatedRoles }));
    }
}
